/**
 * Guided Tour Steps Configuration
 *
 * Defines tour steps for different pages/features.
 * Each tour targets specific elements to guide new users.
 */

export type TourId = 'dashboard' | 'cv-builder' | 'mes-cv' | 'job-first' | 'activation';

export interface TourStep {
  id: string;
  target: string; // CSS selector or data-tour attribute
  title: string;
  content: string;
  icon: string;
  placement: 'top' | 'bottom' | 'left' | 'right' | 'center';
  spotlightPadding?: number;
  // Optional: specific page route (if tour spans multiple pages)
  route?: string;
}

export interface TourConfig {
  id: TourId;
  name: string;
  steps: TourStep[];
  mobileSteps?: TourStep[]; // Simplified steps for mobile
}

// =============================================================================
// DASHBOARD TOUR
// =============================================================================

export const DASHBOARD_TOUR_STEPS: TourStep[] = [
  {
    id: 'sidebar',
    target: '[data-tour="sidebar"]',
    title: 'Barre latérale',
    content: 'Accédez à l\'accueil, au Match CV - Emploi et à votre profil.',
    icon: '🧭',
    placement: 'right',
    spotlightPadding: 8,
  },
  {
    id: 'quick-actions',
    target: '[data-tour="quick-actions"]',
    title: 'Actions rapides',
    content: 'Créez votre CV, analysez les offres, optimisez la compatibilité — matchez et boostez vos candidatures directement depuis ici.',
    icon: '⚡',
    placement: 'bottom',
    spotlightPadding: 12,
  },
  {
    id: 'activity',
    target: '[data-tour="activity"]',
    title: 'Votre activité',
    content: 'Retrouvez vos dernières actions et nos recommandations pour votre profil.',
    icon: '📊',
    placement: 'top',
    spotlightPadding: 8,
  },
  {
    id: 'profile',
    target: '[data-tour="profile"]',
    title: 'Votre profil',
    content: 'Vos informations de recherche en un coup d\'œil. Modifiez vos préférences et votre CV référence à tout moment.',
    icon: '👤',
    placement: 'left',
    spotlightPadding: 8,
  },
  {
    id: 'ready',
    target: '[data-tour="cta"]',
    title: 'Prêt à commencer ?',
    content: 'À vous de jouer ! Importez une offre, et notre IA vous accompagnera pour optimiser votre CV et maximiser vos chances.',
    icon: '🎯',
    placement: 'center',
  },
];

// =============================================================================
// CV BUILDER TOUR
// =============================================================================

export const CV_BUILDER_TOUR_STEPS: TourStep[] = [
  {
    id: 'cv-header',
    target: '[data-tour="cv-header"]',
    title: 'En-tête du CV',
    content: 'Cliquez sur le titre pour le renommer. Le statut de sauvegarde s\'affiche ici automatiquement.',
    icon: '✏️',
    placement: 'bottom',
    spotlightPadding: 8,
  },
  {
    id: 'section-nav',
    target: '[data-tour="section-nav"]',
    title: 'Navigation par sections',
    content: 'Naviguez rapidement entre les sections de votre CV. La barre de progression indique la complétude.',
    icon: '📑',
    placement: 'bottom',
    spotlightPadding: 8,
  },
  {
    id: 'form-section',
    target: '[data-tour="form-section"]',
    title: 'Zone d\'édition',
    content: 'Remplissez chaque section avec vos informations. Cliquez sur les en-têtes pour développer ou réduire.',
    icon: '📝',
    placement: 'right',
    spotlightPadding: 12,
  },
  {
    id: 'resizable-divider',
    target: '[data-tour="resizable-divider"]',
    title: 'Redimensionner',
    content: 'Glissez cette barre pour ajuster la taille des panneaux selon vos préférences.',
    icon: '↔️',
    placement: 'left',
    spotlightPadding: 4,
  },
  {
    id: 'preview-section',
    target: '[data-tour="preview-section"]',
    title: 'Aperçu en temps réel',
    content: 'Visualisez votre CV au format PDF. Vos modifications apparaissent instantanément.',
    icon: '👁️',
    placement: 'left',
    spotlightPadding: 12,
  },
  {
    id: 'template-selector',
    target: '[data-tour="template-selector"]',
    title: 'Modèles et styles',
    content: 'Changez le modèle, la police et les couleurs pour personnaliser l\'apparence de votre CV.',
    icon: '🎨',
    placement: 'bottom',
    spotlightPadding: 8,
  },
  {
    id: 'pdf-download',
    target: '[data-tour="pdf-download"]',
    title: 'Télécharger en PDF',
    content: 'Quand votre CV est prêt, téléchargez-le au format PDF prêt à l\'envoi.',
    icon: '📥',
    placement: 'bottom',
    spotlightPadding: 8,
  },
];

// Simplified mobile version
export const CV_BUILDER_MOBILE_STEPS: TourStep[] = [
  CV_BUILDER_TOUR_STEPS[0], // header
  CV_BUILDER_TOUR_STEPS[1], // section-nav
  CV_BUILDER_TOUR_STEPS[2], // form-section
  {
    ...CV_BUILDER_TOUR_STEPS[6], // pdf-download
    id: 'pdf-download-mobile',
  },
];

// =============================================================================
// MES CV TOUR (CV List Page)
// =============================================================================

export const MES_CV_TOUR_STEPS: TourStep[] = [
  {
    id: 'welcome',
    target: '[data-tour="mes-cv-header"]',
    title: 'Bienvenue dans Mes CV',
    content: 'Gérez tous vos CV depuis cette page. Créez des versions adaptées à chaque poste et suivez leur utilisation.',
    icon: '📄',
    placement: 'bottom',
    spotlightPadding: 8,
  },
  {
    id: 'principal-cv',
    target: '[data-tour="principal-cv"]',
    title: 'Votre CV de référence',
    content: 'Ce CV sert de base à toutes vos analyses. Plus il est complet et détaillé, meilleures seront vos analyses de compatibilité.',
    icon: '⭐',
    placement: 'bottom',
    spotlightPadding: 12,
  },
  {
    id: 'create-cv',
    target: '[data-tour="create-cv-button"]',
    title: 'Créer un nouveau CV',
    content: 'Créez une nouvelle version de votre CV pour l\'adapter à une offre spécifique. Vos informations sont pré-remplies.',
    icon: '➕',
    placement: 'bottom',
    spotlightPadding: 8,
  },
  {
    id: 'cv-list',
    target: '[data-tour="cv-list"]',
    title: 'Vos CV',
    content: 'Tous vos CV sont listés ici. Cliquez sur un CV pour l\'éditer ou utilisez les actions rapides.',
    icon: '📋',
    placement: 'top',
    spotlightPadding: 8,
  },
  {
    id: 'cv-actions',
    target: '[data-tour="cv-card-actions"]',
    title: 'Actions rapides',
    content: 'Éditez, dupliquez ou supprimez vos CV. Définissez un CV par défaut pour vos candidatures.',
    icon: '⚡',
    placement: 'left',
    spotlightPadding: 8,
  },
];

// Simplified mobile version
export const MES_CV_MOBILE_STEPS: TourStep[] = [
  MES_CV_TOUR_STEPS[0], // welcome
  MES_CV_TOUR_STEPS[2], // create-cv
  MES_CV_TOUR_STEPS[3], // cv-list
];

// =============================================================================
// JOB FIRST TOUR (Mes Candidatures)
// =============================================================================

export const JOB_FIRST_TOUR_STEPS: TourStep[] = [
  {
    id: 'welcome',
    target: '[data-tour="job-first-header"]',
    title: 'Bienvenue dans Mes Candidatures',
    content: 'Gérez toutes vos candidatures au même endroit. Ajoutez des offres, suivez leur statut et optimisez vos chances.',
    icon: '🎯',
    placement: 'bottom',
    spotlightPadding: 8,
  },
  {
    id: 'add-job',
    target: '[data-tour="add-job-button"]',
    title: 'Ajouter une offre',
    content: 'Cliquez ici pour ajouter une nouvelle offre d\'emploi. Collez simplement le lien ou le texte de l\'annonce.',
    icon: '➕',
    placement: 'bottom',
    spotlightPadding: 12,
  },
  {
    id: 'job-list',
    target: '[data-tour="job-list"]',
    title: 'Vos candidatures',
    content: 'Retrouvez ici toutes vos offres sauvegardées. Cliquez sur une offre pour voir les détails et lancer l\'analyse IA.',
    icon: '📋',
    placement: 'right',
    spotlightPadding: 8,
  },
  {
    id: 'filters',
    target: '[data-tour="job-filters"]',
    title: 'Filtres et recherche',
    content: 'Filtrez vos candidatures par statut, entreprise ou date. Retrouvez rapidement l\'offre que vous cherchez.',
    icon: '🔍',
    placement: 'bottom',
    spotlightPadding: 8,
  },
  {
    id: 'analysis',
    target: '[data-tour="job-analysis"]',
    title: 'Analyse IA automatique',
    content: 'Dès que vous ouvrez une offre, notre IA analyse automatiquement votre compatibilité et vous guide pour optimiser votre candidature.',
    icon: '🤖',
    placement: 'center',
  },
];

// Simplified mobile version
export const JOB_FIRST_MOBILE_STEPS: TourStep[] = [
  JOB_FIRST_TOUR_STEPS[0], // welcome
  JOB_FIRST_TOUR_STEPS[1], // add-job
  JOB_FIRST_TOUR_STEPS[4], // analysis
];

// =============================================================================
// ACTIVATION TOUR (Mandatory post-onboarding flow)
// Guides user through their first job offer analysis using real UI
// =============================================================================

export const ACTIVATION_TOUR_STEPS: TourStep[] = [
  {
    id: 'activation-welcome',
    target: '[data-tour="sidebar"]',
    title: 'Bienvenue sur FITD !',
    content: 'Votre CV est prêt. Voyons maintenant comment analyser une offre d\'emploi pour maximiser vos chances. Suivez le guide !',
    icon: '🎉',
    placement: 'right',
    spotlightPadding: 8,
    route: '/dashboard',
  },
  {
    id: 'activation-navigate-applications',
    target: '[data-tour="nav-applications"]',
    title: 'Mes Candidatures',
    content: 'C\'est ici que tout se passe. Cliquez sur "Mes Candidatures" pour commencer à analyser des offres d\'emploi.',
    icon: '👆',
    placement: 'right',
    spotlightPadding: 8,
  },
  {
    id: 'activation-add-job',
    target: '[data-tour="add-job-button"]',
    title: 'Ajoutez votre première offre',
    content: 'Cliquez ici pour analyser une offre d\'emploi. Collez simplement le texte de l\'annonce — notre IA fera le reste.',
    icon: '➕',
    placement: 'bottom',
    spotlightPadding: 12,
    route: '/dashboard/applications',
  },
  {
    id: 'activation-analysis-explanation',
    target: '[data-tour="job-analysis"]',
    title: 'L\'analyse IA en action',
    content: 'Dès qu\'une offre est ajoutée, l\'IA compare votre CV avec les exigences du poste. Vous obtenez un score, vos points forts, et des recommandations concrètes.',
    icon: '🤖',
    placement: 'center',
  },
  {
    id: 'activation-complete',
    target: '[data-tour="quick-actions"]',
    title: 'Vous êtes prêt !',
    content: 'Vous connaissez maintenant l\'essentiel. Ajoutez des offres, optimisez votre CV, et décrochez le poste idéal. Bonne chance !',
    icon: '🚀',
    placement: 'bottom',
    spotlightPadding: 12,
    route: '/dashboard',
  },
];

export const ACTIVATION_MOBILE_STEPS: TourStep[] = [
  ACTIVATION_TOUR_STEPS[0], // welcome
  ACTIVATION_TOUR_STEPS[2], // add-job
  ACTIVATION_TOUR_STEPS[4], // complete
];

// =============================================================================
// TOUR REGISTRY
// =============================================================================

export const TOUR_CONFIGS: Record<TourId, TourConfig> = {
  dashboard: {
    id: 'dashboard',
    name: 'Tableau de bord',
    steps: DASHBOARD_TOUR_STEPS,
    mobileSteps: [
      DASHBOARD_TOUR_STEPS[0],
      DASHBOARD_TOUR_STEPS[1],
      DASHBOARD_TOUR_STEPS[4],
    ],
  },
  'cv-builder': {
    id: 'cv-builder',
    name: 'CV Builder',
    steps: CV_BUILDER_TOUR_STEPS,
    mobileSteps: CV_BUILDER_MOBILE_STEPS,
  },
  'mes-cv': {
    id: 'mes-cv',
    name: 'Mes CV',
    steps: MES_CV_TOUR_STEPS,
    mobileSteps: MES_CV_MOBILE_STEPS,
  },
  'job-first': {
    id: 'job-first',
    name: 'Mes Candidatures',
    steps: JOB_FIRST_TOUR_STEPS,
    mobileSteps: JOB_FIRST_MOBILE_STEPS,
  },
  'activation': {
    id: 'activation',
    name: 'Guide d\'activation',
    steps: ACTIVATION_TOUR_STEPS,
    mobileSteps: ACTIVATION_MOBILE_STEPS,
  },
};

/**
 * Get tour steps for a specific tour, with mobile support
 */
export const getTourSteps = (tourId: TourId, isMobile: boolean): TourStep[] => {
  const config = TOUR_CONFIGS[tourId];
  if (!config) return [];
  return isMobile && config.mobileSteps ? config.mobileSteps : config.steps;
};

/**
 * Get tour config by ID
 */
export const getTourConfig = (tourId: TourId): TourConfig | undefined => {
  return TOUR_CONFIGS[tourId];
};
